import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { IdentityProviderName } from '@ftr/contracts/type/account'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemForbiddenType } from '@ftr/data-court-system'
import { unwrapData } from '@ftr/foundation'
import { AppPaths } from '@ftr/routing-paths'
import { Store } from '@ngxs/store'
import { filter, firstValueFrom, switchMap, take } from 'rxjs'
import { AuthenticationService } from '../../services'
import { MfaSetupModalType, SetMfaSetupModal, UserState } from '../../store'

@Component({
  selector: 'ftr-mfa-setup-required',
  // Empty template
  template: '<div></div>',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MfaSetupRequiredComponent implements OnInit {
  @Input() courtSystemId: Uuid
  readonly courtSystemForbiddenType = CourtSystemForbiddenType.MfaNotEnabled

  constructor(
    protected readonly store: Store,
    private readonly router: Router,
    private readonly authenticationService: AuthenticationService,
  ) {}

  async ngOnInit(): Promise<void> {
    const mfaEnabled = await this.authenticationService.multifactorAuthenticationEnabled
    const mfaSetupModalType = await this.getMfaSetupModalType(mfaEnabled)

    this.store
      .dispatch(new SetMfaSetupModal(mfaSetupModalType))
      .pipe(
        switchMap(() => {
          return this.store.select(UserState.mfaSetupModalType)
        }),
        filter(modalShowing => modalShowing === MfaSetupModalType.Closed),
        take(1),
      )
      .subscribe(() => {
        this.router.navigate([`/${AppPaths.CourtSystemForbidden}`], {
          queryParams: {
            forbidden: this.courtSystemForbiddenType,
          },
        })
      })
  }

  async getMfaSetupModalType(mfaEnabled: boolean | undefined): Promise<MfaSetupModalType> {
    const courtSystems = await firstValueFrom(this.store.select(UserState.courtSystems).pipe(unwrapData()))
    const internalCourtSystem = courtSystems.some(courtSystem => courtSystem.id === this.courtSystemId)
    if (this.authenticationService.identityProvider === IdentityProviderName.Cognito) {
      return Promise.resolve(internalCourtSystem ? MfaSetupModalType.Internal : MfaSetupModalType.External)
    } else if (mfaEnabled === undefined) {
      return Promise.resolve(
        internalCourtSystem ? MfaSetupModalType.InternalUnsupportedSSO : MfaSetupModalType.ExternalUnsupportedSSO,
      )
    }

    return Promise.resolve(internalCourtSystem ? MfaSetupModalType.InternalSSO : MfaSetupModalType.ExternalSSO)
  }
}
